import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData, postData } from "../services/request"
import DisputeForm from "../components/DisputeForm"

const PatientRequestDetails = () => {
  const { postId } = useParams()
  const navigate = useNavigate()
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selectedHospitalId, setSelectedHospitalId] = useState("")
  const [selecting, setSelecting] = useState(false)
  const [paying, setPaying] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [disputes, setDisputes] = useState([])
  const [loadingDisputes, setLoadingDisputes] = useState(true)

  const load = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/posts/${postId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        const data = res.result.data?.data || null
        setRequest(data)
        if(data?.selectedHospital){
          setSelectedHospitalId(data.selectedHospital)
        }
      }else{
        setError(res.result.data?.message || 'Could not load request')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const loadDisputes = async () => {
    setLoadingDisputes(true)
    try {
      const url = `${getAPIBaseURL()}/v1/disputes`
      const params = { limit: 200, skip: 0 }
      const res = await getData(url, {}, params)
      if (res.error == "") {
        if (res.result.status === 200) {
          const all = res.result.data?.data || []
          const filtered = all.filter((d) => {
            const dpId = d.postId?._id || d.postId
            return dpId && String(dpId) === String(postId)
          })
          setDisputes(filtered)
        } else {
          setDisputes([])
        }
      } else {
        setDisputes([])
      }
    } finally {
      setLoadingDisputes(false)
    }
  }

  useEffect(() => {
    load()
    loadDisputes()
  }, [postId])

  const selectPrice = async (hospitalId) => {
    // Normalize: server expects the hospital's userId (stored on price.userId)
    let normalizedHospitalId = typeof hospitalId === 'string' ? hospitalId : (hospitalId?._id || hospitalId?.id)
    if (normalizedHospitalId && typeof normalizedHospitalId !== 'string') {
      normalizedHospitalId = `${normalizedHospitalId}`
    }
    setSelecting(true)
    const url = `${getAPIBaseURL()}/v1/posts/select-price`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    params.append('hospitalId', normalizedHospitalId)
    const res = await postData(url, headers, params.toString())
    setSelecting(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setSelectedHospitalId(normalizedHospitalId)
        load()
      }else{
        setError(res.result.data?.message || 'Could not select price')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const payNow = async () => {
    setPaying(true)
    const url = `${getAPIBaseURL()}/v1/posts/initialize-payment`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    const res = await postData(url, headers, params.toString())
    setPaying(false)
    if(res.error == ""){
      if(res.result.status === 200){
        const link = res.result.data?.data?.paymentLink || res.result.data?.data?.authorization_url || res.result.data?.data?.link
        if(link){
          window.location.href = link
        }else{
          setError('Payment link not provided')
        }
      }else{
        setError(res.result.data?.message || 'Could not initialize payment')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleConfirmReceived = async () => {
    setError("")
    setConfirming(true)
    const url = `${getAPIBaseURL()}/v1/posts/confirm-received`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    const res = await postData(url, headers, params.toString())
    setConfirming(false)
    if(res.error == ""){
      if(res.result.status === 200){
        load()
      }else{
        setError(res.result.data?.message || 'Could not confirm receipt')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatCurrency = (n) => {
    const num = Number(n)
    if(isNaN(num)) return n
    return `₦${num.toLocaleString()}`
  }

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-2xl">Request</h2>
          <p className="mt-1 text-sm text-gray-600 break-words">
            Request ID: <span className="font-mono text-gray-900">{request?.requestId || postId}</span>
          </p>
          <p className="mt-1 text-sm text-gray-600">View prices and proceed to payment</p>
          {request?.selectedHospital ? (
            <div className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700">
              <span className="font-semibold text-gray-900">Selected hospital:</span>{' '}
              {request.selectedHospital?.organization || request.selectedHospital?.name || 'Hospital'}
            </div>
          ) : null}
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
          ) : !request ? (
            <div className="text-sm text-gray-500">Request not found</div>
          ) : (
            <>
              <div className="mb-6">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">Status:</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    request.status === 'PAID' ? 'bg-green-100 text-green-700' :
                    request.status === 'DELIVERED' ? 'bg-blue-100 text-blue-700' :
                    request.status === 'RECEIVED' ? 'bg-purple-100 text-purple-700' :
                    request.status === 'PRICED' ? 'bg-yellow-100 text-yellow-700' :
                    request.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {request.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">{request.message}</p>
              </div>

              {/* Received button for patients when status is DELIVERED */}
              {request.status === 'DELIVERED' ? (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="mb-3 text-sm text-gray-700">Your order has been delivered. Please confirm receipt.</p>
                  <button
                    onClick={handleConfirmReceived}
                    disabled={confirming}
                    className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {confirming ? 'Confirming...' : 'Confirm Received'}
                  </button>
                </div>
              ) : null}

              {/* Show prices and payment options only if status is PENDING or PRICED */}
              {(request.status === 'PENDING' || request.status === 'PRICED') ? (
                <>
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">Prices from hospitals</h3>
                    {Array.isArray(request.prices) && request.prices.length > 0 ? (
                      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                        {request.prices.map((p) => {
                          const hospitalName = p.userId?.organization || p.userId?.name || 'Hospital'
                          const hospitalId = typeof p.userId === 'string' ? p.userId : (p.userId?._id || p.userId?.id)
                          return (
                          <li key={p._id} className="flex flex-col gap-3 p-3 sm:p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{hospitalName}</p>
                              {p.comment ? <p className="mt-0.5 text-xs text-gray-500">{p.comment}</p> : null}
                            </div>
                            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:gap-3">
                              <span className="text-sm font-semibold text-gray-900">{formatCurrency(p.price)}</span>
                              {selectedHospitalId === hospitalId ? (
                                <span className="rounded-full border border-black px-3 py-1 text-xs font-semibold text-black">Selected</span>
                              ) : (
                                <button onClick={()=>selectPrice(hospitalId)} className="w-full sm:w-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50" disabled={selecting}>Select</button>
                              )}
                            </div>
                          </li>
                        )})}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No prices yet</p>
                    )}
                  </div>
                  <div className="pt-1">
                    <button onClick={()=>payNow()} disabled={!selectedHospitalId || paying} className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300 sm:w-auto">Pay now</button>
                  </div>
                </>
              ) : request.status === 'PAID' || request.status === 'PAYMENT_CONFIRMED' ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="text-sm font-medium text-green-900">Payment completed. Waiting for delivery.</p>
                  {request.selectedHospital ? (
                    <p className="mt-1 text-xs text-green-700">Hospital: {request.selectedHospital?.organization || request.selectedHospital?.name || 'Hospital'}</p>
                  ) : null}
                </div>
              ) : request.status === 'RECEIVED' ? (
                <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <p className="text-sm font-medium text-purple-900">Order completed. Thank you!</p>
                </div>
              ) : null}

              {/* Disputes for this request */}
              <div className="mt-8">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Disputes for this request</h3>
                {loadingDisputes ? (
                  <p className="text-sm text-gray-500">Loading disputes...</p>
                ) : disputes.length === 0 ? (
                  <p className="text-sm text-gray-500">No disputes submitted yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {disputes.map((dispute) => (
                      <li key={dispute._id} className="flex items-start justify-between gap-4 p-3 sm:p-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {dispute.subject}
                          </p>
                          <p className="mt-1 text-xs text-gray-600 whitespace-pre-line line-clamp-3">
                            {dispute.message}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            Status:{" "}
                            <span className="font-medium">
                              {dispute.status === 'resolved' ? 'Resolved' : 'Open'}
                            </span>
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Dispute Form */}
              <DisputeForm postId={postId} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PatientRequestDetails


